import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowUpRight, ShieldAlert, Landmark, Coins, Umbrella, Handshake, Clock, FileSearch, Scale, Radar, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import CTAButton from "@/components/CTAButton";
import HeroMock from "@/components/landing/HeroMock";
import {
  RiskScreenExplainer, SampleOutputs, Complement, HowItWorks, Deliverables,
} from "@/components/landing/ScreenSections";
import {
  PortfolioTeaser, Sectors, RiskWatchTeaser, MethodLimitations,
} from "@/components/landing/MarketSections";
import { PAIN_POINTS, FAQS, SOURCE_REVIEW_AREAS } from "@/lib/constants";

const PAIN_ICONS = [Landmark, Coins, Coins, Umbrella, Handshake, Clock, FileSearch, Scale];

export default function Landing() {
  const [samplePublic, setSamplePublic] = useState(false);
  useEffect(() => {
    api.get("/config").then(({ data }) => setSamplePublic(!!data.sample_screen_public)).catch(() => {});
  }, []);

  return (
    <div className="text-white">
      <Hero />
      <Boundary />
      <Pain />
      <SourceIntelligence />
      <ThreePaths />
      <RiskScreenExplainer />
      <SampleOutputs visible={samplePublic} />
      <Complement />
      <HowItWorks />
      <Deliverables />
      <PortfolioTeaser />
      <Sectors />
      <RiskWatchTeaser />
      <MethodLimitations />
      <Faq />
      <FinalCta />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden topo-bg border-b border-white/[0.08]">
      <div className="grain absolute inset-0" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
        <div className="lg:col-span-7">
          <div className="kicker mb-6 flex items-center gap-3">
            <span className="inline-block w-1.5 h-1.5 bg-emerald-500" />
            PFAS land-risk intelligence · UK
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" data-testid="hero-title">
            Know your PFAS and brownfield risk <span className="text-white/50">before</span> planning, lenders, insurers or buyers ask.
          </h1>
          <p className="mt-8 text-white text-xl md:text-2xl font-display leading-snug max-w-2xl" data-testid="hero-positioning">
            PFAS-specific intelligence for land and development decisions.
          </p>
          <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
            We help developers, landowners and planning professionals identify potential PFAS source risks, pathways and evidence gaps before they become planning, transaction, funding or remediation problems.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <CTAButton to="/triage" primary testId="hero-cta-triage">Start the Free PFAS Risk Triage</CTAButton>
            <CTAButton to="/risk-screen" testId="hero-cta-risk-screen">Request a PFAS Brownfield Risk Screen</CTAButton>
          </div>
          <p className="mt-6 text-sm text-white/55 font-mono-alt tracking-[0.08em]" data-testid="hero-price-line">
            PFAS Brownfield Risk Screen from <span className="text-white">£1,950 + VAT</span> <span className="text-white/30 px-1">|</span> 5–7 working days
            <span className="text-white/30 px-1">|</span> <Link to="/checklist" data-testid="hero-cta-checklist" className="underline underline-offset-4 hover:text-white">Free checklist</Link>
          </p>
        </div>
        <div className="lg:col-span-5">
          <HeroMock />
        </div>
      </div>
    </section>
  );
}

function Boundary() {
  return (
    <section className="border-b border-white/[0.08] bg-[#0C0E13]" data-testid="boundary-statement">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-4">
          <div className="kicker mb-3 text-emerald-400">Scope · Professional boundary</div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">We do not replace your contaminated-land consultant.</h2>
        </div>
        <div className="lg:col-span-8 lg:pl-8 lg:border-l border-white/10">
          <p className="text-white/80 leading-relaxed text-base md:text-lg">
            PFAS Risk Intelligence provides a specialist desk-based PFAS intelligence layer to help determine whether further professional investigation may be justified.
          </p>
          <p className="mt-4 text-sm text-white/50 leading-relaxed">
            Our work complements conventional contaminated-land due diligence, laboratory testing, regulatory assessment and professional environmental advice.
          </p>
        </div>
      </div>
    </section>
  );
}

function Pain() {
  return (
    <section className="border-b border-white/[0.08]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="kicker mb-4">Why early intelligence matters</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              The danger is not just contamination. It is finding out too late.
            </h2>
            <p className="mt-6 text-white/60 max-w-md">
              The earlier potential PFAS sources, pathways and evidence gaps are identified, the more options remain on planning, price, funding, insurance and strategy.
            </p>
            <div className="mt-8">
              <CTAButton to="/triage" testId="pain-cta-triage">Check Your Site</CTAButton>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.08]">
            {PAIN_POINTS.map((p, i) => {
              const Icon = PAIN_ICONS[i] || ShieldAlert;
              return (
                <div key={p.t} className="bg-[#0A0B0E] p-6 hover-lift hover:bg-[#12141A] group" data-testid={`pain-card-${i}`}>
                  <Icon className="w-5 h-5 text-white/50 group-hover:text-emerald-400 transition-colors" strokeWidth={1.6} />
                  <div className="mt-4 font-display text-lg leading-snug">{p.t}</div>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function SourceIntelligence() {
  return (
    <section id="source-intelligence" className="border-b border-white/[0.08] bg-[#0C0E13]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="kicker mb-4 flex items-center gap-3"><Radar className="w-3.5 h-3.5 text-emerald-400" /> PFAS source intelligence</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="source-intel-title">
            PFAS risk is not just about what happened on the site.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            PFAS can originate from historic or current activities beyond the site boundary. Our review looks for evidence of potential source areas, migration pathways and relevant historic activities that may not be obvious from the site address alone.
          </p>
          <div className="mt-8 border border-emerald-500/30 bg-emerald-500/[0.06] p-5">
            <p className="text-white leading-relaxed" data-testid="source-intel-objective">
              The objective is not to declare PFAS present. It is to identify whether the available evidence justifies further investigation.
            </p>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="kicker mb-4">Potential review areas</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {SOURCE_REVIEW_AREAS.map((a) => (
              <li key={a} className="flex gap-3 text-sm border-b border-white/[0.06] py-2.5">
                <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" strokeWidth={2.5} />
                <span className="text-white/80">{a}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-white/40 leading-relaxed">
            Evidence-weighted: higher-interest indicators include known or probable AFFF use, fire-training grounds, airfields with firefighting activity, firefighting facilities, PFAS-relevant industrial processes and known PFAS investigations nearby. Generic landfill or industrial-estate history is treated as contextual, not as an automatic major trigger.
          </p>
        </div>
      </div>
    </section>
  );
}

function ThreePaths() {
  return (
    <section id="start" className="border-b border-white/[0.08]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="kicker mb-4">Three ways to start</div>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Screen the risk in minutes. Or brief us for a fixed-fee review.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <PathCard tone="emerald" kicker="Free · 3 min"
            title="PFAS Risk Triage Tool"
            body="Answer 12 practical questions and get an immediate, evidence-weighted Red / Amber / Green preliminary view for your site."
            cta="Start Triage" to="/triage" testId="path-triage" />
          <PathCard tone="amber" kicker="Free download"
            title="PFAS Risk Checklist (PDF)"
            body="12 early-warning signs, scoring guide and documents to gather before planning, acquisition or funding decisions."
            cta="Download PDF" to="/checklist" testId="path-checklist" />
          <PathCard tone="red" kicker="From £1,950 + VAT"
            title="PFAS Brownfield Risk Screen"
            body="PFAS-specific desk-based review of a defined site. Red / Amber / Green decision view, evidence gaps and next steps within 5–7 working days."
            cta="Request Risk Screen" to="/risk-screen" testId="path-risk-screen" />
        </div>
      </div>
    </section>
  );
}

function PathCard({ tone, kicker, title, body, cta, to, testId }) {
  const toneMap = {
    emerald: "text-emerald-400 border-emerald-500/30",
    amber:   "text-amber-400 border-amber-500/30",
    red:     "text-red-400 border-red-500/30",
  };
  return (
    <div className="border border-white/10 p-6 flex flex-col hover-lift hover:border-white/25 bg-[#0C0E13]">
      <div className={`kicker ${toneMap[tone]}`}>{kicker}</div>
      <div className="mt-4 font-display text-2xl leading-tight">{title}</div>
      <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{body}</p>
      <Link to={to} data-testid={testId}
        className="mt-6 inline-flex items-center gap-2 text-[12px] font-mono-alt tracking-[0.18em] uppercase text-white group">
        {cta} <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2.5} />
      </Link>
    </div>
  );
}

function Faq() {
  return (
    <section className="border-b border-white/[0.08]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="kicker mb-4">Questions serious buyers ask</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">Straight answers on scope, evidence and limits.</h2>
          <div className="mt-8">
            <CTAButton to="/risk-screen" testId="faq-cta-risk-screen">Request a Risk Screen</CTAButton>
          </div>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="border-t border-white/10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} className="border-b border-white/10">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-lg py-5 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-white/65 leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section id="risk-screen" className="bg-[#0A0B0E]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 border border-white/[0.08] my-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="kicker mb-4">Next move</div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight max-w-lg">
            Find the risk. Trace the source. Recommend the next move.
          </h3>
          <p className="mt-4 text-sm text-white/50">PFAS Brownfield Risk Screen from £1,950 + VAT · 5–7 working days</p>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <CTAButton to="/triage" primary testId="final-cta-triage">Start the Free PFAS Risk Triage</CTAButton>
          <CTAButton to="/risk-screen" testId="final-cta-risk-screen">Request a Risk Screen</CTAButton>
          <CTAButton to="/portfolio-screening" testId="final-cta-portfolio">Discuss Portfolio Screening</CTAButton>
        </div>
      </div>
    </section>
  );
}
